import { memo, useCallback, useId } from 'react';
import { Link } from 'react-router-dom';

import CustomSelect from 'components/custom-select';
import SeletorDeCiclo, { CicloDoSeletor } from 'components/ui/seletor-de-ciclo';
import { FOCO_VISIVEL } from 'utils/foco';

export interface OpcaoDaFila {
  value: string;
  label: string;
}

/** Ciclo como a página o guarda (`value` é o `_id`): o `onCiclo` recebe o objeto inteiro, como o handler de hoje. */
export type CicloDaFila = CicloDoSeletor & OpcaoDaFila;

/** Aviso de lateralidade (ECN-56): a página decide quando mostrar; o "Sair" é o mesmo link do SAIR de hoje. */
export interface LateralidadeDaFilaEcoa {
  /** "validando" ou "apreciando", pela regra do aviso de hoje. */
  verbo: string;
  nome: string;
  chave: string;
  /** "dependência - função". */
  lotacao: string;
}

/** Abas "Diretoria" / "Rede" que a página de hoje mostra quando `hasRede`. */
export interface AbasDaRede {
  ativa: 'diretoria' | 'rede';
  onEscolher: (aba: 'diretoria' | 'rede') => void;
}

export interface SeletoresDaFilaEcoaProps {
  ciclos: CicloDaFila[];
  ciclo: CicloDaFila | undefined;
  onCiclo: (ciclo: CicloDaFila) => void;
  opcoesDeFase: OpcaoDaFila[];
  fase: string | undefined;
  onFase: (valor: string) => void;
  /**
   * Durante a leitura da fila. A leitura de hoje não descarta a resposta de um ciclo ou fase anterior: trocar no meio
   * mostraria a fila errada (a mesma trava da fila de Realização Destaque).
   */
  desabilitado?: boolean;
  lateralidade?: LateralidadeDaFilaEcoa;
  abasDaRede?: AbasDaRede;
}

const ROTULO = 'mb-[7px] block text-[11px] font-bold uppercase tracking-[0.05em] text-content-soft';
const SEGMENTO = `cursor-pointer whitespace-nowrap rounded-jj-md border-0 px-4 py-[9px] font-display text-xs font-bold ${FOCO_VISIVEL}`;

/** Os blocos da fila re-renderizam a cada leitura; o `react-select` só re-renderiza quando as props dele mudam. */
const CampoDeSelecao = memo(CustomSelect);
const CampoDeCiclo = memo(SeletorDeCiclo);

/** "Ciclo", "Mês / fase" (ECN-45) e o aviso de lateralidade (ECN-56) da fila. */
function SeletoresDaFilaEcoa(props: SeletoresDaFilaEcoaProps) {
  const { lateralidade, abasDaRede, ciclos, onCiclo, onFase } = props;
  const aoEscolherCiclo = useCallback(
    (cicloId: string) => {
      const escolhido = ciclos.find(ciclo => ciclo._id === cicloId);
      if (escolhido) onCiclo(escolhido);
    },
    [ciclos, onCiclo],
  );
  const aoEscolherFase = useCallback((opcao: any) => opcao?.value && onFase(opcao.value), [onFase]);
  const avisoId = useId();

  return (
    <section aria-label="Seleção da fila" className="mt-3.5 rounded-jj-panel bg-brand-white px-[22px] pb-4 pt-[18px] shadow-jj-01">
      <div className="flex flex-wrap items-end gap-[18px]">
        <div className="min-w-[180px] flex-[1_1_100%] sm:flex-[0_1_200px]">
          <label htmlFor="fila-ecoa-ciclo" className={ROTULO}>
            Ciclo
          </label>
          <CampoDeCiclo
            ciclos={ciclos}
            valor={props.ciclo?._id}
            onChange={aoEscolherCiclo}
            rotulo="Ciclo"
            inputId="fila-ecoa-ciclo"
            desabilitado={props.desabilitado}
          />
        </div>

        <div className="min-w-[250px] flex-[1_1_260px]">
          <label htmlFor="fila-ecoa-fase" className={ROTULO}>
            Mês / fase
          </label>
          <CampoDeSelecao
            inputId="fila-ecoa-fase"
            ariaLabel="Mês / fase"
            variant="light"
            options={props.opcoesDeFase}
            value={props.opcoesDeFase.find(opcao => opcao.value === props.fase)}
            disabled={props.desabilitado}
            onChange={aoEscolherFase}
          />
        </div>

        {abasDaRede && (
          <div role="group" aria-label="Ações da fase por origem" className="ml-auto inline-flex gap-1 rounded-jj-md bg-surface-100 p-1">
            {(['diretoria', 'rede'] as const).map(aba => (
              <button
                key={aba}
                type="button"
                aria-pressed={abasDaRede.ativa === aba}
                onClick={() => abasDaRede.onEscolher(aba)}
                className={`${SEGMENTO} ${abasDaRede.ativa === aba ? 'bg-brand-blue-dark text-brand-yellow' : 'bg-transparent text-content'}`}
              >
                {aba === 'diretoria' ? 'Diretoria' : 'Rede'}
              </button>
            ))}
          </div>
        )}
      </div>

      {lateralidade && (
        <section aria-labelledby={avisoId} className="mt-4 flex flex-wrap items-start gap-3 rounded-jj-lg bg-brand-yellow-light px-[18px] py-3.5">
          <div className="min-w-0 flex-[1_1_320px]">
            <p id={avisoId} className="m-0 font-display text-[13px] font-bold text-brand-blue-dark">
              {`Você está ${lateralidade.verbo} por:`}
            </p>
            <p className="m-0 mt-1.5 text-[11.5px] leading-[1.6] text-content">
              <b>{lateralidade.nome}</b> ({lateralidade.chave}) · {lateralidade.lotacao}
            </p>
          </div>
          <Link
            to="/"
            className={`self-center rounded-jj-sm border border-brand-blue-dark bg-brand-white px-4 py-2.5 text-[11px] font-bold uppercase tracking-[0.04em] text-brand-blue-dark no-underline hover:text-brand-blue-dark hover:no-underline ${FOCO_VISIVEL}`}
          >
            Sair
          </Link>
        </section>
      )}
    </section>
  );
}

/** Memoizado: a página da fila re-renderiza a cada leitura e a cada segundo, e o `react-select` é caro. */
export default memo(SeletoresDaFilaEcoa);
